import { create } from 'zustand'
import { timetableApi } from '@/services/api/timetable'
import type { PeriodGroup, TeacherValidityUpdate } from '@/services/api/timetable'
import { isValidUUID } from '@/utils/uuid'

interface ValidityEditState {
  // Data
  periodGroups: PeriodGroup[]
  isLoading: boolean
  error: string | null
  isApplying: boolean
  applyProgress: string | null
  
  // Selection - Compound Keys: "validFrom|validTo|teacherId"
  selectedKeys: Set<string>
  
  // UI State
  expandedGroups: Set<string>  // "validFrom|validTo"
  filter: 'all' | 'current' | 'future' | 'expired' | 'duplicates'
  showOnlySelected: boolean
  batchSize: number
  
  // Edit
  newValidFrom: string
  newValidTo: string
  mode: 'update' | 'replace'
  
  // Actions
  toggleGroup: (validFrom: string | null, validTo: string | null) => void
  toggleTeacher: (validFrom: string | null, validTo: string | null, teacherId: string) => void
  selectAllInGroup: (validFrom: string | null, validTo: string | null, selected: boolean) => void
  selectAll: () => void
  deselectAll: () => void
  loadPeriodGroups: (teacherIds: string[]) => Promise<void>
  applyChanges: () => Promise<{ success: boolean; updatedCount: number; changeId?: string; expiresAt?: string; errors?: any[] }>
  setNewValidity: (from: string, to: string) => void
  setMode: (mode: 'update' | 'replace') => void
  setFilter: (filter: 'all' | 'current' | 'future' | 'expired' | 'duplicates') => void
  setShowOnlySelected: (show: boolean) => void
  setBatchSize: (size: number) => void
  reset: () => void
}

// Helper functions for compound keys
const makeGroupKey = (validFrom: string | null, validTo: string | null) => 
  `${validFrom || 'null'}|${validTo || 'null'}`

const makeSelectionKey = (
  validFrom: string | null, 
  validTo: string | null, 
  teacherId: string
) => `${makeGroupKey(validFrom, validTo)}|${teacherId}`

const parseSelectionKey = (key: string): { validFrom: string | null; validTo: string | null; teacherId: string } | null => {
  const parts = key.split('|')
  if (parts.length < 3 || !parts[2]) return null
  const from = parts[0] ?? ''
  const to = parts[1] ?? ''
  return {
    validFrom: from === 'null' ? null : from,
    validTo: to === 'null' ? null : to,
    teacherId: parts[2],
  }
}

export const useValidityEditStore = create<ValidityEditState>((set, get) => ({
  // Initial state
  periodGroups: [],
  isLoading: false,
  error: null,
  isApplying: false,
  applyProgress: null,
  selectedKeys: new Set(),
  expandedGroups: new Set(),
  filter: 'all',
  showOnlySelected: false,
  batchSize: 50,
  newValidFrom: '',
  newValidTo: '',
  mode: 'update',
  
  // Actions
  toggleGroup: (validFrom, validTo) => {
    const key = makeGroupKey(validFrom, validTo)
    const { expandedGroups } = get()
    const next = new Set(expandedGroups)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.add(key)
    }
    set({ expandedGroups: next })
  },
  
  toggleTeacher: (validFrom, validTo, teacherId) => {
    const key = makeSelectionKey(validFrom, validTo, teacherId)
    const { selectedKeys } = get()
    const next = new Set(selectedKeys)
    if (next.has(key)) {
      next.delete(key)
    } else {
      next.add(key)
    }
    set({ selectedKeys: next })
  },
  
  selectAllInGroup: (validFrom, validTo, selected) => {
    const groupKey = makeGroupKey(validFrom, validTo)
    const { periodGroups, selectedKeys } = get()
    const next = new Set(selectedKeys)
    
    const group = periodGroups.find(g => 
      makeGroupKey(g.valid_from, g.valid_to) === groupKey
    )
    
    if (!group) return
    
    group.teachers.forEach(teacher => {
      const key = makeSelectionKey(validFrom, validTo, teacher.teacher_id)
      if (selected) {
        next.add(key)
      } else {
        next.delete(key)
      }
    })
    
    set({ selectedKeys: next })
  },
  
  selectAll: () => {
    const { periodGroups } = get()
    const next = new Set<string>()
    
    periodGroups.forEach(group => {
      group.teachers.forEach(teacher => {
        next.add(makeSelectionKey(group.valid_from, group.valid_to, teacher.teacher_id))
      })
    })
    
    set({ selectedKeys: next })
  },
  
  deselectAll: () => {
    set({ selectedKeys: new Set() })
  },
  
  loadPeriodGroups: async (teacherIds: string[]) => {
    set({ isLoading: true, error: null })
    try {
      // Filter out invalid teacher IDs before calling the API
      const validTeacherIds = teacherIds.filter(id => isValidUUID(id))
      
      if (validTeacherIds.length === 0) {
        set({ periodGroups: [], isLoading: false })
        return
      }
      
      const response = await timetableApi.getPeriodGroups(validTeacherIds, false)
      set({ periodGroups: response.groups, isLoading: false })
    } catch (err: any) {
      let errorMessage = 'Perioden konnten nicht geladen werden'
      
      if (err.response?.status === 400) {
        errorMessage = 'Ungültige Lehrer-ID'
      } else if (err.response?.status === 403) {
        errorMessage = 'Keine Berechtigung'
      } else if (err.message) {
        errorMessage = err.message
      }
      
      set({ error: errorMessage, isLoading: false })
    }
  },
  
  applyChanges: async () => {
    const { selectedKeys, newValidFrom, newValidTo, mode, batchSize } = get()
    
    // Compute selectedUpdates inline (getter doesn't work reliably after set())
    const updates: TeacherValidityUpdate[] = []
    selectedKeys.forEach(key => {
      const parsed = parseSelectionKey(key)
      if (!parsed) return
      updates.push({
        teacher_id: parsed.teacherId,
        old_valid_from: parsed.validFrom,
        old_valid_to: parsed.validTo,
        new_valid_from: newValidFrom || null,
        new_valid_to: newValidTo || null,
      })
    })
    
    if (updates.length === 0) {
      return { success: false, updatedCount: 0, changeId: undefined, errors: [] }
    }
    
    set({ isApplying: true, applyProgress: 'Preparing...' })
    
    try {
      // Split into batches
      const batches: TeacherValidityUpdate[][] = []
      for (let i = 0; i < updates.length; i += batchSize) {
        batches.push(updates.slice(i, i + batchSize))
      }
      
      let totalUpdated = 0
      const allErrors: any[] = []
      let lastChangeId: string | undefined = undefined
      let lastExpiresAt: string | undefined = undefined
      
      for (let i = 0; i < batches.length; i++) {
        set({ applyProgress: `Batch ${i + 1} of ${batches.length}...` })
        
        const batch = batches[i]!
        const response = await timetableApi.bulkUpdateValidity({
          updates: batch,
          mode,
        })
        
        totalUpdated += response.updated_count
        if (response.errors) {
          allErrors.push(...response.errors)
        }
        
        // Capture change_id from the last batch
        if (response.change_id) {
          lastChangeId = response.change_id
          lastExpiresAt = response.expires_at
        }
      }
      
      set({ 
        isApplying: false, 
        applyProgress: null,
        selectedKeys: new Set(), // Clear selection after success
      })
      
      return { 
        success: allErrors.length === 0, 
        updatedCount: totalUpdated, 
        changeId: lastChangeId,
        expiresAt: lastExpiresAt,
        errors: allErrors 
      }
    } catch (err: any) {
      set({ isApplying: false, applyProgress: null })
      return { 
        success: false, 
        updatedCount: 0, 
        changeId: undefined,
        expiresAt: undefined,
        errors: [{ teacher_id: '', error: err.message }] 
      }
    }
  },
  
  setNewValidity: (from: string, to: string) => {
    set({ newValidFrom: from, newValidTo: to })
  },
  
  setMode: (mode: 'update' | 'replace') => {
    set({ mode })
  },
  
  setFilter: (filter: 'all' | 'current' | 'future' | 'expired' | 'duplicates') => {
    set({ filter })
  },
  
  setShowOnlySelected: (show: boolean) => {
    set({ showOnlySelected: show })
  },
  
  setBatchSize: (size: number) => {
    set({ batchSize: size })
  },
  
  reset: () => {
    set({
      periodGroups: [],
      isLoading: false,
      error: null,
      isApplying: false,
      applyProgress: null,
      selectedKeys: new Set(),
      expandedGroups: new Set(),
      filter: 'all',
      showOnlySelected: false,
      batchSize: 50,
      newValidFrom: '',
      newValidTo: '',
      mode: 'update',
    })
  },
}))
